import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCircleCheck } from '@fortawesome/free-solid-svg-icons';

import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import useAlert from '../../../Alerts/useAlert';
import usePrivileges from '../../../Auth/usePrivileges';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { LotStatus } from '../../../Types/Lot';

interface BlockLotButtonProps {
    setLoading: Dispatch<SetStateAction<boolean>>
}

function BlockLotButton({ setLoading }: BlockLotButtonProps) {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { canReview } = usePrivileges();

    const { lotDetail: { summary: { lotNumber, currentStatus, isBlocked } }, getLotDetail } = useContext(LotDetailContext);

    const blockLot = async () => {
        setLoading(true);

        fetchDataAuthenticated(`api/v1/lot/${lotNumber}/block`, 'PUT', JSON.stringify({}))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Lot ${lotNumber} is now blocked`);
                    getLotDetail();
                } else {
                    let message = await response.text();
                    throw new Error(`Failed to block the lot. ${message}`);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const unblockLot = async () => {
        setLoading(true);

        fetchDataAuthenticated(`api/v1/lot/${lotNumber}/unblock`, 'PUT', JSON.stringify({}))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Lot ${lotNumber} has been unblocked`);
                    getLotDetail();
                } else {
                    let message = await response.text();
                    throw new Error(`Failed to unblock the lot. ${message}`);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const [canBlock, setCanBlock] = useState(!!canReview && currentStatus !== LotStatus.Graded);
    useEffect(() => {
        setCanBlock(!!canReview && currentStatus !== LotStatus.Graded);
    }, [canReview, currentStatus]);

    return isBlocked
        ? <Button variant="warning"
            hidden={!canBlock}
            onClick={() => unblockLot()}>
            <FontAwesomeIcon icon={faCircleCheck} className="me-1" />
            {t<string>('LotDetails.Summary.UnblockLot')}
        </Button>
        : <Button variant="warning"
            hidden={!canBlock}
            onClick={() => blockLot()}>
            <FontAwesomeIcon icon={faBan} className="me-1" />
            {t<string>('LotDetails.Summary.BlockLot')}
        </Button>
}

export default BlockLotButton;